import React, { useEffect, useMemo, useState } from "react";
import MenuGroupTabs, { useMenuGroups } from "./menuGroupTabs";

import {
  filterMenuItems,
  formatMoney,
  formatNumber,
  getItems,
  getNumber,
  getOutletMetric,
} from "../menuEngineeringUtils";
import {
  RANKS,
  RANK_ACTIONS,
  getAdvice,
  getChange,
  getConfidence,
  getCostCoverage,
  getFoodCost,
  getItemMix,
  getPeriodDays,
  getPreviousRanks,
  getQuantityNeeded,
  getRankChange,
  getRecommendations,
  hasCost,
  isModifier,
  simulatePrice,
} from "./menuDashboardInsights";
import {
  ClassSummary,
  ConfidenceCallout,
  CostCoverage,
  Delta,
  Legend,
  RankBadge,
  RankIcon,
  Recommendations,
} from "./menuDashboardParts";
import { exportCsv, exportExcel, exportPdf } from "./menuDashboardExport";
import "../tokens.css";
import "./menuDashboard.scss";

export { isModifier, RankBadge };

const QUADRANTS = [
  ["topLeft", "Puzzle", "Puzzles", "High margin, few sales. Promote."],
  ["topRight", "Star", "Stars", "High margin, high sales. Protect."],
  ["bottomLeft", "Dog", "Dogs", "Low margin, few sales. Review."],
  [
    "bottomRight",
    "Plowhorse",
    "Plowhorses",
    "Low margin, high sales. Re-cost.",
  ],
];

const getOutletKey = (outlet, index) =>
  String(outlet?.chaKey || outlet?.name || `outlet-${index}`);

const formatSummaryAmount = (value) =>
  new Intl.NumberFormat("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(getNumber(value));

// Keep the decision line centered while preserving the values on either side.
export const getMatrixPosition = (value, line, min, max) =>
  value <= line
    ? 8 + (42 * (value - min)) / (line - min || 1)
    : 50 + (42 * (value - line)) / (max - line || 1);

const getTotals = (outlet, items, useItems) => {
  const source = useItems ? { items } : outlet || {};
  const revenue = getOutletMetric(source, "sales", "totalSales");
  const cost = getOutletMetric(source, "cost", "totalCost");
  return {
    quantity: getOutletMetric(source, "quantity", "quantity"),
    revenue,
    profit: getOutletMetric(source, "profit", "totalProfit"),
    cost,
    foodCost: revenue ? cost / revenue : 0,
  };
};

const getTableTotals = (visibleItems, menuQuantity) => {
  if (!visibleItems.length) return [];
  const allQuantity = visibleItems.reduce(
    (total, item) => total + getNumber(item.quantity),
    0
  );
  const summarize = (items, includeCost) => {
    const sum = (key) =>
      items.reduce((total, item) => total + getNumber(item[key]), 0);
    const quantity = sum("quantity");
    const sales = sum("totalSales");
    const cost = sum("totalCost");
    const profit = sum("totalProfit");
    return {
      count: items.length,
      quantity,
      share: items.reduce(
        (total, item) => total + getItemMix(item, menuQuantity),
        0
      ),
      sales,
      cost: includeCost ? cost : null,
      foodCost: includeCost && sales ? cost / sales : null,
      avgProfit: includeCost && quantity ? profit / quantity : null,
      profit: includeCost ? profit : null,
      unknownPlateShare:
        !includeCost && allQuantity ? quantity / allQuantity : 0,
    };
  };
  const costed = visibleItems.filter(hasCost);
  const uncosted = visibleItems.filter((item) => !hasCost(item));
  return [
    costed.length && {
      key: "costed",
      label: "All dishes with a cost",
      ...summarize(costed, true),
    },
    uncosted.length && {
      key: "uncosted",
      label: "No cost on file",
      ...summarize(uncosted, false),
    },
  ].filter(Boolean);
};

const ITEMS_PER_PAGE = 25;

function MenuDashboard({
  outlets,
  previousOutlets = null,
  period = null,
  filter = null,
}) {
  const [selectedOutletKey, setSelectedOutletKey] = useState("");
  const [rankFilter, setRankFilter] = useState("All");
  const [itemsPage, setItemsPage] = useState(1);
  const [selectedItemKey, setSelectedItemKey] = useState(null);
  const [hoveredItemKey, setHoveredItemKey] = useState(null);
  const [includeModifiers, setIncludeModifiers] = useState(false);
  const [whatIf, setWhatIf] = useState({ itemKey: null, adjust: 0 });
  const [exportError, setExportError] = useState("");
  const selectedOutlet =
    outlets.find(
      (outlet, index) => getOutletKey(outlet, index) === selectedOutletKey
    ) || outlets[0];
  const outletKey = selectedOutlet
    ? getOutletKey(selectedOutlet, outlets.indexOf(selectedOutlet))
    : "";
  const outletItems = useMemo(
    () => getItems(selectedOutlet ? [selectedOutlet] : []),
    [selectedOutlet]
  );
  const eligibleItems = useMemo(
    () => outletItems.filter((item) => includeModifiers || !isModifier(item)),
    [outletItems, includeModifiers]
  );
  const { items, tabs, isFiltered } = useMenuGroups(
    eligibleItems,
    selectedOutlet
  );
  useEffect(() => {
    setItemsPage(1);
  }, [outletKey, rankFilter, includeModifiers, tabs.page, tabs.category]);
  const modifiersExcluded = !includeModifiers && outletItems.some(isModifier);
  const useItemTotals = isFiltered || modifiersExcluded;
  const totals = getTotals(selectedOutlet, items, useItemTotals);
  const menuQuantity = getOutletMetric(
    selectedOutlet || {},
    "quantity",
    "quantity"
  );

  const previousOutlet = previousOutlets?.find((outlet, index) =>
    selectedOutlet?.chaKey
      ? outlet.chaKey === selectedOutlet.chaKey
      : getOutletKey(outlet, index) === outletKey
  );
  const previousOutletItems = useMemo(
    () => getItems(previousOutlet ? [previousOutlet] : []),
    [previousOutlet]
  );
  const previousTotals = previousOutlet
    ? getTotals(
        previousOutlet,
        filterMenuItems(
          previousOutletItems.filter(
            (item) => includeModifiers || !isModifier(item)
          ),
          tabs.page,
          tabs.category
        ),
        useItemTotals
      )
    : null;
  const previousRanks = useMemo(
    () => (previousOutlet ? getPreviousRanks(previousOutletItems) : null),
    [previousOutlet, previousOutletItems]
  );

  const getRule = (key, fallback) => {
    const value = selectedOutlet?.rules?.[key];
    return value != null && Number.isFinite(Number(value))
      ? Number(value)
      : fallback;
  };
  const popularityLine = getRule(
    "menuMix",
    outletItems.length ? 70 / outletItems.length : 0
  );
  const marginLine = getRule(
    "profitContribution",
    menuQuantity
      ? getOutletMetric(selectedOutlet || {}, "profit", "totalProfit") /
          menuQuantity
      : 0
  );
  const lines = { items, popularityLine, marginLine, menuQuantity };
  const qtyNeeded = getQuantityNeeded(popularityLine, menuQuantity);
  const days = getPeriodDays(period);
  const coverage = getCostCoverage(items);
  const recommendations = getRecommendations(lines);

  const visibleItems = items.filter(
    (item) => rankFilter === "All" || item._rank === rankFilter
  );
  const selectedItem =
    visibleItems.find((item) => item._itemKey === selectedItemKey) ||
    visibleItems[0];
  const adjust =
    selectedItem && whatIf.itemKey === selectedItem._itemKey
      ? whatIf.adjust
      : 0;
  const simulation = selectedItem
    ? simulatePrice(selectedItem, adjust, lines)
    : null;
  const rankCounts = Object.fromEntries(
    RANKS.map((rank) => [
      rank,
      items.filter((item) => item._rank === rank).length,
    ])
  );
  const tableTotals = getTableTotals(visibleItems, menuQuantity);
  // Used by the hidden class mix card.
  const classChanges = previousRanks
    ? items.filter(
        (item) => getRankChange(item, previousRanks)?.type === "moved"
      ).length
    : 0;
  const sortedItems = [...items].sort(
    (a, b) => getNumber(b.totalProfit) - getNumber(a.totalProfit)
  );
  const sortedVisibleItems = sortedItems.filter((item) =>
    visibleItems.includes(item)
  );
  const pageCount = Math.max(
    1,
    Math.ceil(sortedVisibleItems.length / ITEMS_PER_PAGE)
  );
  const currentItemsPage = Math.min(itemsPage, pageCount);
  const pagedItems = sortedVisibleItems.slice(
    (currentItemsPage - 1) * ITEMS_PER_PAGE,
    currentItemsPage * ITEMS_PER_PAGE
  );

  const profits = items
    .map((item) => getNumber(item.avgProfit))
    .concat(simulation ? [simulation.margin] : []);
  const mixes = items.map((item) => getItemMix(item, menuQuantity));
  const minProfit = Math.min(0, marginLine - 1, ...profits);
  const maxProfit = Math.max(marginLine + 1, ...profits);
  const minMix = Math.min(0, popularityLine - 1, ...mixes);
  const maxMix = Math.max(popularityLine + 1, ...mixes);
  const maxContribution = Math.max(
    1,
    ...items.map((item) => Math.abs(getNumber(item.totalProfit)))
  );
  const getBubble = (item, margin = getNumber(item.avgProfit)) => ({
    left: getMatrixPosition(
      getItemMix(item, menuQuantity),
      popularityLine,
      minMix,
      maxMix
    ),
    bottom: getMatrixPosition(margin, marginLine, minProfit, maxProfit),
    size:
      16 +
      36 * Math.sqrt(Math.abs(getNumber(item.totalProfit)) / maxContribution),
  });
  const hoveredItem = visibleItems.find(
    (item) => item._itemKey === hoveredItemKey
  );

  const selectItem = (itemKey) => {
    setRankFilter((current) =>
      current === "All" ||
      items.find((item) => item._itemKey === itemKey)?._rank === current
        ? current
        : "All"
    );
    setSelectedItemKey(itemKey);
  };
  const changeAdjust = (delta) => {
    if (!selectedItem) return;
    setWhatIf({
      itemKey: selectedItem._itemKey,
      adjust: delta == null ? 0 : adjust + delta,
    });
  };
  const viewName =
    [
      tabs.pages.find((group) => group.key === tabs.page)?.name,
      tabs.categories.find((group) => group.key === tabs.category)?.name,
    ]
      .filter(Boolean)
      .join(" / ") || "All items";
  const report = {
    items: sortedItems,
    menuQuantity,
    totals,
    popularityLine,
    marginLine,
    coverage,
    meta: {
      outlet: selectedOutlet?.name || outletKey,
      view: viewName,
      period,
      days,
    },
  };

  return (
    <div className="menuDashboard menuDashboardV2">
      <MenuGroupTabs {...tabs} />
      <div className="menuDashboardToolbar">
        <label>
          <span className="menuDashboardToolbarLabel">Outlet</span>
          <span style={{ marginLeft: 25, opacity: 0.75 }}>
            <select
              aria-label="Select outlet"
              value={outletKey}
              onChange={(event) => {
                setSelectedOutletKey(event.target.value);
                setSelectedItemKey(null);
                setRankFilter("All");
              }}
            >
              {outlets.map((outlet, index) => {
                const key = getOutletKey(outlet, index);
                return (
                  <option key={key} value={key}>
                    {outlet.name || key}
                  </option>
                );
              })}
            </select>
          </span>
        </label>
        <div>
          <span>{formatNumber(items.length)} items in view</span>
          <button
            type="button"
            className="menuModifierToggle"
            aria-pressed={includeModifiers}
            onClick={() => setIncludeModifiers((value) => !value)}
          >
            Modifiers {includeModifiers ? "on" : "off"}
          </button>
          <div className="menuExport" role="group" aria-label="Export">
            {[
              ["CSV", exportCsv],
              ["Excel", exportExcel],
              ["PDF", exportPdf],
            ].map(([label, run]) => (
              <button
                type="button"
                key={label}
                disabled={!items.length}
                onClick={() => {
                  setExportError("");
                  Promise.resolve(run(report)).catch(() =>
                    setExportError(`${label} export failed. Please try again.`)
                  );
                }}
              >
                {label}
              </button>
            ))}
            {/* <button
              type="button"
              disabled={!items.length}
              onClick={() => window.print()}
            >
              Print
            </button> */}
          </div>
        </div>
      </div>
      {items.length > 0 && <CostCoverage coverage={coverage} />}
      {exportError && (
        <div className="menuConfidence low" role="alert">
          {exportError}
        </div>
      )}
      {/* <p className="menuDashboardNote">Modifiers are items with a reference above 3600. Classifications and decision lines use the outlet’s results.</p> */}

      {!items.length ? (
        <div className="menuEngineeringEmpty compact">
          <h2>No menu items found</h2>
          <p>
            Try another page, category, date range, or outlet selection, or
            include modifiers.
          </p>
        </div>
      ) : (
        <>
          <ConfidenceCallout confidence={getConfidence(days)} days={days} />
          <section className="menuKpis" aria-label="Key figures">
            <Kpi
              label="Revenue"
              value={formatMoney(totals.revenue)}
              delta={
                previousTotals && (
                  <Delta
                    value={toPercent(
                      getChange(totals.revenue, previousTotals.revenue)
                    )}
                  />
                )
              }
              note={`${formatNumber(totals.quantity)} items sold`}
            />
            <Kpi
              label="Contribution margin"
              value={formatMoney(totals.profit)}
              delta={
                previousTotals && (
                  <Delta
                    value={toPercent(
                      getChange(totals.profit, previousTotals.profit)
                    )}
                  />
                )
              }
              note={
                totals.revenue
                  ? `${formatNumber((totals.profit / totals.revenue) * 100, 1)}% of revenue`
                  : "No sales total"
              }
            />
            <Kpi
              label="Food cost"
              value={
                totals.revenue
                  ? `${formatNumber(totals.foodCost * 100, 1)}%`
                  : "—"
              }
              delta={
                previousTotals?.revenue ? (
                  <Delta
                    value={(totals.foodCost - previousTotals.foodCost) * 100}
                    suffix=" pts"
                    invert
                  />
                ) : null
              }
              note={`${formatMoney(totals.cost)} total cost`}
            />
            <Kpi
              label="Popularity line"
              value={`${formatNumber(popularityLine, 1)}%`}
              note={`About ${formatNumber(qtyNeeded)} sold to qualify`}
            />
            <Kpi
              label="Margin line"
              value={formatMoney(marginLine)}
              note="Outlet profit per item sold"
            />
          </section>
          {previousTotals && (
            <p className="menuDashboardNote">
              {filter?.data?.comparison
                ? `Compared with ${filter.data.comparison.name} ${filter.data.comparison.year}: ${filter.data.comparison.businessDates.start} - ${filter.data.comparison.businessDates.end}.`
                : `Changes compare with the previous ${days ? `${days}-day ` : ""}period.`}
            </p>
          )}
          <section className="menuDashboardGrid">
            <div className="menuDashboardColumn menuDashboardMain">
              <div className="menuCard menuMatrixCard">
                <div className="menuCardHeader">
                  <div>
                    <h2>The matrix</h2>
                    <p>
                      Bubble size shows total contribution. Hover for details,
                      select to focus.
                    </p>
                  </div>
                </div>
                <div
                  className="menuRankFilters"
                  aria-label="Filter matrix by rank"
                >
                  {[
                    "All",
                    ...RANKS.filter(
                      (rank) => rank !== "Unrated" || rankCounts.Unrated
                    ),
                  ].map((rank) => (
                    <button
                      type="button"
                      key={rank}
                      className={rankFilter === rank ? "active" : ""}
                      aria-pressed={rankFilter === rank}
                      onClick={() => setRankFilter(rank)}
                    >
                      {rank !== "All" && (
                        <i className={`rank-dot rank-${rank.toLowerCase()}`} />
                      )}
                      {rank}
                      <span>
                        {rank === "All" ? items.length : rankCounts[rank]}
                      </span>
                    </button>
                  ))}
                </div>
                <div
                  className="menuMatrix"
                  aria-label="Menu performance matrix"
                >
                  {QUADRANTS.map(([position, rank, title, text]) => (
                    <div
                      key={rank}
                      className={`menuMatrixQuadrant ${position} rank-${rank.toLowerCase()}`}
                    >
                      <strong>
                        <RankIcon rank={rank} size={13} /> {title}
                      </strong>
                      <span>{text}</span>
                    </div>
                  ))}
                  <span className="menuDecisionLine popularity">
                    Popularity {formatNumber(popularityLine, 1)}%
                  </span>
                  <span className="menuDecisionLine margin">
                    Margin {formatMoney(marginLine)}
                  </span>
                  {adjust !== 0 &&
                    selectedItem &&
                    visibleItems.includes(selectedItem) &&
                    (() => {
                      const origin = getBubble(selectedItem);
                      return (
                        <span
                          className="menuBubbleGhost"
                          aria-hidden="true"
                          style={{
                            left: `${origin.left}%`,
                            bottom: `${origin.bottom}%`,
                            width: origin.size,
                            height: origin.size,
                          }}
                        />
                      );
                    })()}
                  {visibleItems.map((item) => {
                    const isSelected = selectedItem?._itemKey === item._itemKey;
                    const bubble = getBubble(
                      item,
                      isSelected && simulation ? simulation.margin : undefined
                    );
                    return (
                      <button
                        type="button"
                        key={item._itemKey}
                        className={`menuBubble rank-${item._rank.toLowerCase()}${isSelected && adjust ? " simulated" : ""}`}
                        style={{
                          left: `${bubble.left}%`,
                          bottom: `${bubble.bottom}%`,
                          width: bubble.size,
                          height: bubble.size,
                        }}
                        aria-label={`${item.itemDescription}, ${item._rank}`}
                        aria-pressed={isSelected}
                        onMouseEnter={() => setHoveredItemKey(item._itemKey)}
                        onMouseLeave={() => setHoveredItemKey(null)}
                        onFocus={() => setHoveredItemKey(item._itemKey)}
                        onBlur={() => setHoveredItemKey(null)}
                        onClick={() => selectItem(item._itemKey)}
                      >
                        <span className="menuBubbleLabel">
                          {item.itemDescription || item.itemRef}
                        </span>
                      </button>
                    );
                  })}
                  {hoveredItem && (
                    <MatrixTooltip
                      item={hoveredItem}
                      position={getBubble(hoveredItem)}
                      mix={getItemMix(hoveredItem, menuQuantity)}
                      change={getRankChange(hoveredItem, previousRanks)}
                    />
                  )}
                </div>
                <div className="menuMatrixCaption">
                  <span>Fewer guests choose it</span>
                  <strong>Popularity / menu mix</strong>
                  <span>More guests choose it</span>
                </div>
                <ClassSummary items={items} />
                <p className="menuDashboardNote">
                  Vertical axis: average profit per item. Dashed lines mark
                  outlet thresholds; bubbles retain their API classification.
                </p>
              </div>
              {/* Recipe cost coverage and class mix card, hidden for now; restore together with
                  classChanges above. */}
              {/* <aside className="menuCard menuHealth" aria-label="Data health">
                <div className="menuSnapshot">
                  <div className="menuCardEyebrow">CLASS MIX</div>
                  {RANKS.filter((rank) => rankCounts[rank]).map((rank) => (
                    <div className="menuSnapshotRow" key={rank}>
                      <RankBadge rank={rank} />
                      <strong>{rankCounts[rank]}</strong>
                    </div>
                  ))}
                  {previousRanks && (
                    <p className="menuDashboardNote">
                      {classChanges
                        ? `${classChanges} ${classChanges === 1 ? "dish" : "dishes"} changed class since the previous period.`
                        : "No class changes since the previous period."}
                    </p>
                  )}
                </div>
              </aside> */}

              <Legend
                popularityLine={popularityLine}
                marginLine={marginLine}
                qtyNeeded={qtyNeeded}
                days={days}
              />
            </div>
            <div className="menuDashboardColumn menuDashboardSide">
              <aside
                className="menuCard menuItemDetail"
                aria-label="Dish in focus"
              >
                <div className="menuItemDetailHeader">
                  <div>
                    <div className="menuCardEyebrow">DISH IN FOCUS</div>
                    <h2>{selectedItem?.itemDescription || "Select a dish"}</h2>
                  </div>
                  {selectedItem && (
                    <div className="menuItemDetailBadges">
                      <RankBadge rank={selectedItem._rank} />
                      {isModifier(selectedItem) && (
                        <span className="menuModifierBadge">Modifier</span>
                      )}
                      <ClassChange
                        change={getRankChange(selectedItem, previousRanks)}
                      />
                    </div>
                  )}
                </div>
                {selectedItem ? (
                  <>
                    <div className="menuDetailMetrics">
                      <Metric
                        label="Sold / menu share"
                        value={`${formatNumber(selectedItem.quantity)} · ${formatNumber(getItemMix(selectedItem, menuQuantity), 1)}%`}
                        note={`line ${formatNumber(popularityLine, 1)}%`}
                      />
                      <Metric
                        label="Margin per item"
                        value={formatMoney(selectedItem.avgProfit)}
                        note={`line ${formatMoney(marginLine)}`}
                      />
                      <Metric
                        label="Food cost"
                        value={
                          getFoodCost(selectedItem) == null
                            ? "—"
                            : `${formatNumber(getFoodCost(selectedItem) * 100)}%`
                        }
                        note={
                          hasCost(selectedItem)
                            ? `cost ${formatMoney(selectedItem.avgCost)}`
                            : "cost not on file"
                        }
                      />
                      <Metric
                        label="Total contribution"
                        value={formatMoney(selectedItem.totalProfit)}
                        note={
                          totals.profit
                            ? `${formatNumber((getNumber(selectedItem.totalProfit) / totals.profit) * 100, 1)}% of view`
                            : ""
                        }
                      />
                    </div>
                    <div className="menuWhatIf">
                      <div>
                        <span className="menuCardEyebrow">WHAT-IF PRICE</span>
                        <strong
                          className={adjust !== 0 ? "menuSimulationActive" : ""}
                        >
                          {formatMoney(simulation.price)}
                        </strong>
                        {adjust !== 0 && (
                          <em>
                            {adjust > 0 ? "+" : ""}
                            {adjust} simulated
                          </em>
                        )}
                      </div>
                      <div className="menuWhatIfControls">
                        <button
                          type="button"
                          aria-label="Lower price by 1"
                          onClick={() => changeAdjust(-1)}
                        >
                          −
                        </button>
                        <button
                          type="button"
                          aria-label="Raise price by 1"
                          onClick={() => changeAdjust(1)}
                        >
                          +
                        </button>
                        <button
                          type="button"
                          onClick={() => changeAdjust(null)}
                          disabled={!adjust}
                        >
                          Reset
                        </button>
                      </div>
                      {adjust !== 0 && (
                        <p>
                          Margin {formatMoney(simulation.margin)} per item,{" "}
                          {simulation.impact >= 0 ? "+" : "−"}QAR{" "}
                          {formatNumber(Math.abs(simulation.impact))} at current
                          volume.
                          {simulation.rank !== selectedItem._rank && (
                            <>
                              {" "}
                              Would sit with the{" "}
                              <RankBadge rank={simulation.rank} /> against
                              today’s lines.
                            </>
                          )}
                        </p>
                      )}
                      <small>
                        Estimate only: assumes volume holds and decision lines
                        stay fixed.
                      </small>
                    </div>
                    <div className="menuRecommendedMove">
                      <span>RECOMMENDED MOVE</span>
                      <strong>{RANK_ACTIONS[selectedItem._rank]}</strong>
                      <p>{getAdvice(selectedItem, lines)}</p>
                    </div>
                  </>
                ) : (
                  <p>No dishes match this classification.</p>
                )}
              </aside>
              <Recommendations
                recommendations={recommendations}
                onPick={selectItem}
              />
            </div>
          </section>
          <section className="menuCard menuDashboardItems">
            <div className="menuCardHeader">
              <div>
                <h2>All dishes</h2>
                <p>
                  Sorted by total contribution. Select a dish to inspect it.
                </p>
              </div>
              <span className="menuTableCount">
                {visibleItems.length} items
              </span>
            </div>
            <div className="menuTableScroll">
              <table>
                <thead>
                  <tr>
                    {[
                      "Dish",
                      "Class",
                      "Sold",
                      "Share",
                      "Price",
                      "Cost",
                      "Food cost",
                      "Margin",
                      "Contribution",
                      "Action",
                    ].map((label) => (
                      <th key={label}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map((item) => {
                    const foodCost = getFoodCost(item);
                    return (
                      <tr
                        key={item._itemKey}
                        className={
                          selectedItem?._itemKey === item._itemKey
                            ? "selected"
                            : ""
                        }
                      >
                        <td>
                          <button
                            className="menuDishButton"
                            type="button"
                            onClick={() => selectItem(item._itemKey)}
                          >
                            {item.itemDescription || "—"}
                          </button>
                          {isModifier(item) && (
                            <small className="menuModifierBadge">
                              Modifier
                            </small>
                          )}
                          <ClassChange
                            change={getRankChange(item, previousRanks)}
                            compact
                          />
                        </td>
                        <td>
                          <RankBadge rank={item._rank} />
                        </td>
                        <td>{formatNumber(item.quantity)}</td>
                        <td>
                          {formatNumber(getItemMix(item, menuQuantity), 1)}%
                        </td>
                        <td>{formatMoney(item.avgPrice)}</td>
                        <td>
                          {hasCost(item)
                            ? formatMoney(item.avgCost)
                            : "Not available"}
                        </td>
                        <td className={foodCost > 0.35 ? "menuBad" : ""}>
                          {foodCost == null
                            ? "—"
                            : `${formatNumber(foodCost * 100)}%`}
                        </td>
                        <td>{formatMoney(item.avgProfit)}</td>
                        <td>{formatMoney(item.totalProfit)}</td>
                        <td>{RANK_ACTIONS[item._rank]}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {tableTotals.length > 0 && (
                  <tfoot>
                    {tableTotals.map((row) => (
                      <tr className={`menuTableTotal ${row.key}`} key={row.key}>
                        <td colSpan={2}>
                          <span className="menuTableSummaryLabel">
                            {row.label}
                            <small>
                              {row.count} {row.count === 1 ? "dish" : "dishes"}
                            </small>
                          </span>
                        </td>
                        <td>{formatNumber(row.quantity)}</td>
                        <td>{formatNumber(row.share, 1)}%</td>
                        <td>{formatSummaryAmount(row.sales)}</td>
                        <td>
                          {row.cost == null
                            ? "—"
                            : formatSummaryAmount(row.cost)}
                        </td>
                        <td className={row.foodCost > 0.35 ? "menuBad" : ""}>
                          {row.foodCost == null
                            ? "—"
                            : `${formatNumber(row.foodCost * 100)}%`}
                        </td>
                        <td>
                          {row.avgProfit == null
                            ? "—"
                            : formatSummaryAmount(row.avgProfit)}
                        </td>
                        <td>
                          {row.profit == null
                            ? "—"
                            : formatSummaryAmount(row.profit)}
                        </td>
                        <td>
                          <span className="menuTableSummaryNote">
                            {row.key === "costed"
                              ? `Margin line QAR ${formatSummaryAmount(row.avgProfit)} = QAR ${formatSummaryAmount(row.profit)} / ${formatNumber(row.quantity)} plates`
                              : `${formatNumber(row.unknownPlateShare * 100)}% of plates have unknown margin`}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tfoot>
                )}
              </table>
            </div>
            {pageCount > 1 && (
              <nav
                className="menuTablePagination"
                aria-label="All dishes pages"
              >
                <button
                  type="button"
                  onClick={() => setItemsPage(currentItemsPage - 1)}
                  disabled={currentItemsPage === 1}
                >
                  Previous
                </button>
                <span aria-live="polite">
                  Page {currentItemsPage} of {pageCount}
                </span>
                <button
                  type="button"
                  onClick={() => setItemsPage(currentItemsPage + 1)}
                  disabled={currentItemsPage === pageCount}
                >
                  Next
                </button>
              </nav>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const toPercent = (value) => (value == null ? null : value * 100);

function MatrixTooltip({ item, position, mix, change }) {
  return (
    <div
      className={`menuMatrixTooltip${position.left > 60 ? " flip" : ""}`}
      role="tooltip"
      style={{ left: `${position.left}%`, bottom: `${position.bottom}%` }}
    >
      <strong>{item.itemDescription || item.itemRef}</strong>
      <RankBadge rank={item._rank} />
      <span>
        {formatNumber(item.quantity)} sold · {formatNumber(mix, 1)}% share
      </span>
      <span>
        {formatMoney(item.avgProfit)} margin · {formatMoney(item.totalProfit)}{" "}
        total
      </span>
      {change && (
        <span>
          {change.type === "new" ? "New this period" : `Was ${change.from}`}
        </span>
      )}
    </div>
  );
}

function ClassChange({ change, compact = false }) {
  if (!change) return null;
  return (
    <small className={`menuClassChange ${change.type}`}>
      {change.type === "new"
        ? "New"
        : compact
          ? `was ${change.from}`
          : `Moved from ${change.from}`}
    </small>
  );
}

function Kpi({ label, value, note, delta }) {
  return (
    <article className="menuKpi">
      <span>{label}</span>
      <strong>{value}</strong>
      {delta}
      <small>{note}</small>
    </article>
  );
}

function Metric({ label, value, note }) {
  return (
    <div className="menuDetailMetric">
      <span>{label}</span>
      <strong>{value}</strong>
      {note && <small>{note}</small>}
    </div>
  );
}

export default MenuDashboard;
